/**
 * Sessão do Painel Gerencial — regras puras + a ponte entre o `authedFetch`
 * (módulo sem React) e o AuthProvider. Mesmo desenho do hub (care-aderencia,
 * lib/sessao.ts) e do CRM (lib/sessao.js), 06-07/09/2026.
 *
 * O que derrubava a tela aqui (até 15/09/2026): o JWT durava 8 h (2 h desde
 * 17/09/2026, backend/auth.py) e nada o renovava antes de vencer. O `authedFetch` até tentava renovar num 401, mas
 * quando falhava devolvia o 401 e cada tela reagia do seu jeito ("recarregue
 * a página (F5)"), e o `/api/auth/me` da abertura, ao falhar, limpava a sessão
 * e jogava no login sem tentar a conta Microsoft que continua no cache.
 *
 * Agora:
 *  - o front LÊ o `exp` do token e renova sozinho a ~10 min do fim (MSAL
 *    silencioso → /api/auth/microsoft-login), com jitter: o rate-limit do
 *    login é 5/min por IP e o escritório inteiro sai por um IP só;
 *  - um 401 renova e REPETE a chamada uma vez (401 é recusa na porta, o
 *    handler não rodou — repetir é seguro);
 *  - se a renovação falhar, a tela FICA montada e um aviso oferece
 *    "Continuar conectado"; ir para o login é decisão do operador.
 */

export const RENOVAR_ANTES_MS = 10 * 60 * 1000; // renova sozinho a 10 min do fim
export const JITTER_MS = 90 * 1000; // espalha as renovações (rate-limit por IP compartilhado)
export const AVISO_MS = 5 * 60 * 1000; // a pílula vira alerta
export const RETENTAR_APOS_MS = 60 * 1000; // 1ª retentativa; dobra a cada falha (ver retentarApos)
export const RETENTAR_TETO_MS = 8 * 60 * 1000;

export const EVENTO_SESSAO_EXPIRADA = 'sessao:expirada';
// A chave já existia no Login.js: destino guardado antes do redirect do MSAL.
export const CHAVE_RETORNO = 'login_redirect_from';

/** Instante (ms) em que o JWT expira; null se o token não for legível. */
export function expiracaoDoToken(token) {
  if (!token) return null;
  try {
    const partes = token.split('.');
    if (partes.length < 2) return null;
    const b64 = partes[1].replace(/-/g, '+').replace(/_/g, '/');
    const preenchido = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const exp = Number(JSON.parse(atob(preenchido)).exp);
    if (!Number.isFinite(exp) || exp <= 0) return null;
    return exp * 1000;
  } catch {
    return null;
  }
}

export function faseDaSessao(restanteMs) {
  if (restanteMs === null || restanteMs === undefined) return 'ok';
  if (restanteMs <= 0) return 'expirada';
  if (restanteMs <= AVISO_MS) return 'aviso';
  return 'ok';
}

/** "7h 42min" acima de 1 h; "42 min" entre 5 e 59 min; "4:59" abaixo de 5 min. */
export function formatarRestante(ms) {
  if (!Number.isFinite(ms) || ms <= 0) return '0:00';
  const totalSeg = Math.floor(ms / 1000);
  const min = Math.floor(totalSeg / 60);
  if (min >= 60) {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}min`;
  }
  if (ms > AVISO_MS) return `${min} min`;
  const seg = totalSeg % 60;
  return `${min}:${String(seg).padStart(2, '0')}`;
}

/**
 * Espera antes da N-ésima retentativa (1, 2, 3…): 60 s, 120 s, 240 s… até 8 min.
 * Fixo em 60 s, várias abas retentando um 429/5xx transitório encheriam o
 * rate-limit do login (5/min por IP, bloqueio de 5 min) em vez de esvaziá-lo.
 */
export function retentarApos(tentativa) {
  const n = Math.max(1, Math.floor(Number(tentativa) || 1));
  return Math.min(RETENTAR_APOS_MS * 2 ** (n - 1), RETENTAR_TETO_MS);
}

/**
 * Quando disparar a renovação automática. Sempre no futuro (>= agora + 1 s):
 * um token já dentro da janela renova logo, e o jitter só ATRASA dentro da
 * janela para não empilhar todo mundo no mesmo minuto.
 */
export function instanteDeRenovacao(expMs, agoraMs, aleatorio = Math.random()) {
  const fracao = Math.max(0, Math.min(1, Number(aleatorio) || 0));
  const alvo = expMs - RENOVAR_ANTES_MS + Math.floor(fracao * JITTER_MS);
  return Math.max(agoraMs + 1000, alvo);
}

/**
 * Qual conta do cache do MSAL é a DO USUÁRIO LOGADO. O cache é o localStorage
 * do navegador: numa máquina onde duas pessoas da Care já entraram, a primeira
 * da lista pode ser a outra, e a tela passaria a operar como ela. Com e-mail
 * salvo, só a conta desse e-mail serve; sem e-mail, só se houver uma conta.
 */
export function contaDoUsuario(contas, emailLogado) {
  const lista = Array.isArray(contas) ? contas : [];
  if (emailLogado) {
    const alvo = String(emailLogado).toLowerCase();
    return lista.find((c) => (c && c.username ? String(c.username) : '').toLowerCase() === alvo);
  }
  return lista.length === 1 ? lista[0] : undefined;
}

/**
 * Normaliza pelo parser de URL do navegador e só aceita a MESMA origem:
 * "//evil.com" e "/\\evil.com" (barra invertida vira barra no WHATWG) resolvem
 * para https://evil.com e o react-router faria `location.assign` para fora.
 * Só quem escreve no sessionStorage (mesma origem) chega aqui, mas guarda é
 * guarda. /login nunca é destino de retorno.
 */
export function caminhoSeguro(v) {
  if (!v || typeof window === 'undefined') return null;
  try {
    const origem = window.location.origin;
    const u = new URL(v, origem);
    if (u.origin !== origem) return null;
    if (u.pathname === '/login' || u.pathname.startsWith('/login/')) return null;
    return u.pathname + u.search;
  } catch {
    return null;
  }
}

/** Guarda para onde voltar depois de um login forçado pela expiração (caminho + query). */
export function guardarRetorno(caminho) {
  const seguro = caminhoSeguro(caminho);
  if (!seguro) return;
  try { sessionStorage.setItem(CHAVE_RETORNO, seguro); } catch { /* storage indisponível */ }
}

export function consumirRetorno() {
  if (typeof window === 'undefined') return null;
  try {
    const v = sessionStorage.getItem(CHAVE_RETORNO);
    if (v) sessionStorage.removeItem(CHAVE_RETORNO);
    return caminhoSeguro(v);
  } catch {
    return null;
  }
}

// ── Ponte authedFetch ↔ AuthProvider ────────────────────────────────────────
// O `authedFetch` vive fora do React. O provider registra aqui a função que
// renova (MSAL silencioso + backend); a promessa é compartilhada para N
// chamadas concorrentes com 401 dispararem UMA renovação — e não N logins no
// rate-limit de 5/min por IP.
let _renovador = null;
let _leitorDeToken = null;
let _emAndamento = null;
// Token que a ÚLTIMA renovação bem-sucedida produziu. Um 401 com ESTE token
// ainda vivo não é sessão vencida — é a rota dizendo outra coisa (o sync do
// Google Calendar devolve 401 quando o Google não está autorizado). Renovar de
// novo só gastaria login.
let _tokenDaUltimaRenovacao = null;

export function registrarRenovador(fn) {
  _renovador = fn;
}

export function registrarLeitorDeToken(fn) {
  _leitorDeToken = fn;
}

/** Só para teste: esquece renovador, leitor e o token da última renovação. */
export function _resetarPonte() {
  _renovador = null;
  _leitorDeToken = null;
  _emAndamento = null;
  _tokenDaUltimaRenovacao = null;
}

export function renovarSessao() {
  if (_emAndamento) return _emAndamento;
  if (!_renovador) return Promise.resolve(false);
  _emAndamento = Promise.resolve()
    .then(() => _renovador())
    .then((ok) => {
      if (ok && _leitorDeToken) _tokenDaUltimaRenovacao = _leitorDeToken();
      return !!ok;
    })
    .catch(() => false)
    .finally(() => { _emAndamento = null; });
  return _emAndamento;
}

export function avisarSessaoExpirada() {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(EVENTO_SESSAO_EXPIRADA));
}

/**
 * Executa uma chamada autenticada; num 401, renova a sessão e repete UMA vez.
 * Devolve SEMPRE a Response (as 15 telas do painel leem `res.ok`/`res.status`;
 * ReunioesTab usa o 401 do sync do Google para pedir autorização) — nunca lança
 * por causa da sessão.
 *  - 401 é recusa na porta (o handler não rodou): repetir não duplica nada.
 *  - Renovou e o 401 continuou: não é sessão, é a rota. Devolve o 2º 401 com a
 *    sessão intacta — e o token recém-renovado fica marcado: o próximo 401
 *    com ele nem renova.
 *  - Não renovou: se o token usado já tinha vencido, avisa o provider (aviso
 *    em cima da tela, sem navegar); devolve o 401 original.
 */
export async function comSessaoRenovada(executar, lerToken) {
  const tokenUsado = lerToken();
  const primeira = await executar(tokenUsado);
  if (primeira.status !== 401) return primeira;
  const exp = expiracaoDoToken(tokenUsado);
  const vivo = exp !== null && exp > Date.now() + 60 * 1000;
  if (vivo && tokenUsado && tokenUsado === _tokenDaUltimaRenovacao) return primeira;
  // Outra chamada (ou o timer) já renovou enquanto esta estava em voo: o token
  // atual é outro, então basta repetir — sem gastar mais um /microsoft-login.
  const tokenAgora = lerToken();
  const renovou = tokenAgora && tokenAgora !== tokenUsado ? true : await renovarSessao();
  if (!renovou) {
    if (!vivo) avisarSessaoExpirada();
    return primeira;
  }
  return executar(lerToken());
}
